import React from 'react'
import { useFormikContext } from 'formik'
import { cloneDeep } from 'lodash'

import { FormGroupBootstrap, MdMath } from '../components'
import { DECIMAL_PLACES } from '../globals'
import { post_new_problema } from '../api'
import type { EnsureDefined } from '../utils/type_utilities'

type FormValues_ = Parameters<typeof post_new_problema>[0]

export type ATTR = "soluzioni" | "soluzioni_scelta_multipla";

type ProblemaNestedNum = EnsureDefined<FormValues_["problema"], "soluzioni">
interface FormValuesNum extends FormValues_ {
  problema: ProblemaNestedNum
}

type ProblemaNestedMul = EnsureDefined<FormValues_["problema"], "soluzioni_scelta_multipla">
export interface FormValuesMul extends FormValues_ {
  problema: ProblemaNestedMul
}


const defaultValueSoluzioni = (count: number) => {
  return {
    label: `Domanda ${count + 1}`,
    udm: "$\\SI{}{}$",
    analitica: "",
    soluzione: "",
    soluzione_exp: 0,
    precisione: 0.005 as unknown as string,
  } as FormValuesNum["problema"]["soluzioni"][0]
}

const AcceptedResponsePreview = ({ answer, index }: {
  answer: FormValuesNum["problema"]["soluzioni"][0];
  index: number;
}) => {
  const numericFields = [answer.soluzione, answer.soluzione_exp, answer.precisione];
  const value = Number(answer.soluzione) * Math.pow(10, Number(answer.soluzione_exp));
  const precision = Number(answer.precisione);
  const divided = value / (1 + precision);
  const multiplied = value * (1 + precision);
  const complete = numericFields.every(field => field !== '' && field != null && Number.isFinite(Number(field)))
    && precision >= 0 && [value, divided, multiplied].every(Number.isFinite)
    && (Number(answer.soluzione) === 0 || value !== 0);
  // Round only the preview; retain the original Formik values for submission.
  const mathNumber = (number: number) => String(Number(number.toPrecision(5)))
    .replace(/e([+-]?\d+)/i, '\\cdot 10^{$1}');
  const rawUnit = (answer.udm || '').trim().replace(/^\${1,2}|\${1,2}$/g, '').trim();
  const unit = rawUnit ? `\\,${/^\\(?:SI|si)\s*\{/.test(rawUnit) ? rawUnit : `\\si{${rawUnit}}`}` : '';
  const interval = value === 0 ? `x = 0${unit}`
    : `${mathNumber(Math.min(divided, multiplied))}${unit} \\leq x \\leq ${mathNumber(Math.max(divided, multiplied))}${unit}`;

  return (
    <div className="bg-light border rounded p-3 mb-3 text-center" role="region" aria-label={`Risposta accettata ${index + 1}`}>
      <h6>Risposta accettata</h6>
      {complete ? <div className="overflow-auto"><MdMath source={`$${interval}$`} /></div>
        : <p className="text-muted mb-0">Completa valore, esponente e precisione per vedere l’intervallo di accettazione.</p>}
    </div>
  );
};


const idxToFormSoluzioni = (idx: number, attr_: ATTR) => {
  const attr = attr_ as "soluzioni";
  const build_name = (idx: number, name: string) => `problema.${attr}[${idx}].${name}`
  const formik = useFormikContext();
  const { setFieldValue } = formik;
  const values = formik.values as FormValuesNum;


  return (
    <div className="card my-2" key={idx}>
      <div className="card-body">
        <button type="button" onClick={() => {
          const arrcopy = cloneDeep(values.problema[attr]);
          arrcopy.splice(idx, 1)
          setFieldValue(`problema.${attr}`, arrcopy)
        }}
          className="ml-auto btn btn-danger my-2"
          aria-label={`Rimuovi risposta ${idx + 1}`}
        >
          <i className="fa fa-times" />
        </button>

        <FormGroupBootstrap
          name={build_name(idx, "label")}
          help_text="Un indicatore che indicherai nel testo"
          type="text"
        />
        <div className="form-row">
          <div className="col-12 col-lg-5">
            <div className="d-flex align-items-start">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <FormGroupBootstrap
                  name={build_name(idx, "soluzione")} displayName="Valore"
                  type="number" step={DECIMAL_PLACES}
                  help_text="Cifre significative."
                />
              </div>
              <div className="px-2 flex-shrink-0" style={{ paddingTop: '2rem' }} aria-hidden="true">
                <MdMath source={"$\\cdot 10$"} />
              </div>
              <div style={{ flex: '0 1 140px', minWidth: 0, transform: 'translateY(-6px)' }}>
                <FormGroupBootstrap
                  name={build_name(idx, "soluzione_exp")} displayName=" "
                  type="number" step={1}
                  help_text="Potenza di dieci."
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-lg-4">
            <FormGroupBootstrap
              name={build_name(idx, "udm")} displayName=" "
              help_text="Unità di misura in LaTeX."
              type="text"
              extra_append={
                <div className="input-group-append">
                  <div className="input-group-text">
                    <MdMath source={values.problema[attr][idx].udm || ""} />
                  </div>
                </div>
              }
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <FormGroupBootstrap
              name={build_name(idx, "precisione")} displayName="Precisione"
              type="number" step={DECIMAL_PLACES}
              help_text="Tolleranza relativa p."
            />
          </div>
        </div>
        <div className="small text-muted mb-3">
          <p className="mb-1">
            Scrivi la risposta in notazione scientifica: il valore viene moltiplicato per dieci elevato all’esponente.
            Per esempio, valore 2.5 ed esponente 3 indicano 2500. Mantieni un numero elevato di cifre significative.
          </p>
          <p className="mb-1">
            Indica le unità in LaTeX, per esempio <code>{'$\\SI{}{m/s}$'}</code>;
            per una risposta adimensionale usa <code>{'$\\SI{}{}$'}</code>.
            Preferisci unità di base, come m anziché km, per non suggerire la risposta.
          </p>
          <p className="mb-0">
            La precisione p è una tolleranza relativa: 0.005 corrisponde allo 0.5%.
            Il controllo richiede che il rapporto tra risposta e valore atteso sia compreso tra 1/(1 + p) e 1 + p;
            per zero è accettato solo zero. I limiti dell’anteprima sono arrotondati a 5 cifre dopo la virgola.
          </p>
        </div>
        <AcceptedResponsePreview answer={values.problema[attr][idx]} index={idx} />
        <FormGroupBootstrap
          name={build_name(idx, "analitica")} displayName="Soluzione analitica"
          type="text"
          help_text="Se applicabile, indicare la soluzione analitica a questo punto del problema, in formule. Utilizzare il LaTeX includendo fra dollari il testo."
        />
        <div className="text-center">
          <MdMath source={values.problema[attr][idx].analitica || ""} />
        </div>
      </div>
    </div>
  )
}


export const SoluzioniFactory = (attr: ATTR, defaultValue: (_: number) => any, idxToForm: (idx: number, attr: ATTR) => React.JSX.Element) => () => {
  const formik = useFormikContext();
  const { setFieldValue } = formik;
  const values = formik.values as FormValuesNum | FormValuesMul;
  // Questo accade sulla pagina delle bozze
  if (!values.problema) {
    setFieldValue("problema", { [attr]: [] })
    return <></>
  }
  const count = !!values.problema[attr] ? (values.problema[attr] as any[]).length : 0
  return (
    <>
      <div className="d-flex col-sm py-2">
        <button type="button" onClick={() => {
          setFieldValue(`problema.${attr}[${count}]`, defaultValue(count))
        }}
          className="mr-auto btn btn-success">Aggiungi una risposta</button>
      </div>
      {count >= 0 && Array.from(Array(count).keys()).map(idx => idxToForm(idx, attr as "soluzioni"))}
    </>
  )

}


const MultipleSoluzioniForm = SoluzioniFactory("soluzioni", defaultValueSoluzioni, idxToFormSoluzioni);


export default MultipleSoluzioniForm
